import type { Character } from '../domain/character.ts';
import { findInventoryEntry, findSkillRank } from '../domain/character.ts';
import type { CharacterRepository } from '../persistence/characterRepository.ts';
import type { CommitOutcome } from './characterModificationService.ts';
import { commitChanges } from './characterModificationService.ts';
import type { HotlistCatalogue, HotlistEntry, HotlistOwnership } from '../rules/hotlist.ts';
import { hotlistAddProblem, hotlistSlotIndexProblem } from '../rules/hotlist.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import { unallocatedCopies } from '../rules/inventory.ts';
import type { Skill } from '../content/types.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { inEncounter } from '../domain/runtime.ts';

/**
 * Configuring the ten quick-access slots.
 *
 * CONFIGURATION ONLY, and that has not changed. Nothing HERE activates,
 * consumes, swaps in combat, or spends an Action: RULE DCC-HOT-018 (C&C p.15)
 * says Hotlist membership grants nothing by itself.
 *
 * What a slot can be USED for lives elsewhere, deliberately - four different
 * pathways, none of them this file:
 *
 *  - the free Attack weapon swap (E3, `weaponSwap` + `attackActionService`);
 *  - drinking a prepared supported consumable straight from its slot, which is
 *    the ordinary item-use pathway (E4, `itemUseService` via
 *    `handleUseRequest`) - the panel raises a request and nothing more;
 *  - the paid Ready / Swap Action, which puts an Item physically in a hand
 *    (E5, `hotlistUseService`);
 *  - Cast a Spell (E6, not built).
 *
 * A slot is a shortcut. The buttons that act on one belong to the services that
 * can charge for them, and none of that moves in here.
 *
 * Every operation goes through `commitChanges` and therefore through the
 * `CharacterChange` union, so legality is checked in one place, the write is
 * atomic, the audit log records it, and Undo restores the exact previous
 * arrangement. Nothing writes `character.hotlist` directly.
 */

export interface HotlistDeps {
  characters: CharacterRepository;
  now: () => string;
  catalogue: InventoryCatalogue;
  lookupSkill: (skillId: string) => Skill | undefined;
  /**
   * REQUIRED, and required as the real thing.
   *
   * RULE DCC-HOT-021 - Rules Table 3 makes **Retrieve** an Action: moving an
   * item from Inventory to the Hotlist costs one IN combat, while Rules p.47
   * makes it free outside it. E2 deliberately does not build that priced
   * pathway, so the conservative answer is the one Inventory already uses
   * (RULE DCC-INV-013): ordinary configuration is simply unavailable during an
   * encounter until the Action that pays for it exists.
   *
   * A boolean flag would be a bypass route and there is no `ignoreEncounter`
   * parameter, deliberately. The priced pathways will be their OWN operations
   * that take the runtime, spend the Action and commit both records - not this
   * one with a mode.
   */
  runtime: RuntimeState;
}

/**
 * RULE DCC-HOT-021. Returns the refusal, or null when configuration is allowed.
 *
 * THE SERVICE IS THE BOUNDARY. The panel's grey controls are an explanation;
 * this is what a second caller cannot get around.
 */
export function hotlistLockedByEncounter(runtime: RuntimeState): string | null {
  if (!inEncounter(runtime)) return null;
  return (
    'Hotlist configuration is unavailable during an encounter. Putting something INTO the ' +
    'Hotlist from the Inventory mid-fight is the Retrieve Action (Rules p.19, p.37), which is ' +
    'not automated yet. Something already prepared can still be used: drink or activate a ' +
    "supported consumable with the slot's Use control, or draw and exchange an Item with " +
    'Ready / Swap. Either costs one Action in combat (Rules p.48).'
  );
}

/**
 * The ONE preflight every configuration operation runs, before any change is
 * built and long before anything is written.
 *
 * It establishes two facts in order, and the order matters:
 *
 *  1. **The runtime belongs to this crawler.** A `RuntimeState` is only
 *     evidence about the crawler it names. Requiring the real object rather
 *     than a boolean was the whole point of the encounter lock - a flag can be
 *     wrong, an object can be checked - but that only holds if somebody checks
 *     WHOSE it is. Handed a stale runtime left over from a screen that switched
 *     crawlers, the encounter question below would be answered about the wrong
 *     person, and a quiet runtime would wave a change through while THIS
 *     crawler is mid-fight. Worded as an internal fault, deliberately: nothing
 *     the player did was against the rules, and saying otherwise would be a lie
 *     about their crawler. Same shape and same wording as `inventoryService`.
 *  2. **The crawler is not in an encounter** (RULE DCC-HOT-021).
 *
 * Centralised so all three operations establish the same facts. Three separate
 * copies of this is how one of them ends up missing a check.
 */
function hotlistPreflight(character: Character, deps: HotlistDeps): string | null {
  if (deps.runtime.characterId !== character.id) {
    return (
      `Internal error: this Hotlist change is for ${character.id} but the runtime supplied ` +
      `belongs to ${deps.runtime.characterId}. Nothing has been changed. Reopen the crawler ` +
      'and try again; if it keeps happening, report it.'
    );
  }
  return hotlistLockedByEncounter(deps.runtime);
}

/** What the crawler currently has. RULE DCC-HOT-002 - the Hotlist is part of
 *  the Inventory, so it cannot name things the crawler does not own. */
export function ownershipOf(character: Character): HotlistOwnership {
  return {
    // EQUIPPED COPIES SUBTRACTED. A crawler owning one Longsword and wielding
    // it has no spare, so that sword cannot also be sitting in the Hotlist:
    // there is one physical object and it is in their hand.
    availableCopies: (itemId) => {
      const entry = findInventoryEntry(character, itemId);
      return entry === undefined ? 0 : unallocatedCopies(entry);
    },
    knowsSkill: (skillId) => findSkillRank(character, skillId) > 0,
  };
}

function catalogueOf(deps: HotlistDeps): HotlistCatalogue {
  return { lookupItem: deps.catalogue.lookupItem, lookupSkill: deps.lookupSkill };
}

function refuse(reason: string): CommitOutcome {
  return { ok: false, problems: [{ index: 0, message: reason }] };
}

/**
 * Puts something in a slot.
 *
 * THE ACQUISITION PATHWAY, and it is strict: the reference must resolve, the
 * crawler must own it, a Spell slot must hold an actual Spell, and nothing may
 * already occupy another slot. The change kind underneath is looser on purpose
 * so that Undo and import can preserve entries this would refuse to create -
 * see `set-hotlist-slot` in `src/rules/characterChanges.ts`.
 */
export async function setHotlistSlot(
  character: Character,
  slot: number,
  entry: HotlistEntry,
  deps: HotlistDeps,
): Promise<CommitOutcome> {
  const blocked = hotlistPreflight(character, deps);
  if (blocked) return refuse(blocked);

  const problem = hotlistAddProblem(
    character.hotlist,
    slot,
    entry,
    catalogueOf(deps),
    ownershipOf(character),
  );
  if (problem) return refuse(problem);

  return commitChanges(
    character,
    [
      {
        change: { kind: 'set-hotlist-slot', slot, entry },
        source: 'manual-edit',
        reason: `Hotlist slot ${slot + 1}`,
      },
    ],
    { characters: deps.characters, now: deps.now },
    { catalogue: deps.catalogue, lookupSkill: deps.lookupSkill },
  );
}

/**
 * Empties a slot.
 *
 * Deliberately has NO validation beyond the slot index. An unresolved or
 * no-longer-owned entry must remain removable, or a player who imported a
 * crawler from a machine with content this one lacks would be stuck with a
 * shortcut they can neither use nor clear.
 */
export async function clearHotlistSlot(
  character: Character,
  slot: number,
  deps: HotlistDeps,
): Promise<CommitOutcome> {
  const blocked = hotlistPreflight(character, deps);
  if (blocked) return refuse(blocked);

  const problem = hotlistSlotIndexProblem(slot);
  if (problem) return refuse(problem);

  return commitChanges(
    character,
    [
      {
        change: { kind: 'set-hotlist-slot', slot, entry: null },
        source: 'manual-edit',
        reason: `Cleared Hotlist slot ${slot + 1}`,
      },
    ],
    { characters: deps.characters, now: deps.now },
    { catalogue: deps.catalogue, lookupSkill: deps.lookupSkill },
  );
}

/**
 * Moves an entry, or exchanges two.
 *
 * One operation covers both, because moving into an empty slot IS a swap with
 * nothing. Order carries no mechanical meaning in the source, so this is the
 * player arranging their own sheet.
 */
export async function moveHotlistSlot(
  character: Character,
  from: number,
  to: number,
  deps: HotlistDeps,
): Promise<CommitOutcome> {
  const blocked = hotlistPreflight(character, deps);
  if (blocked) return refuse(blocked);

  const problem = hotlistSlotIndexProblem(from) ?? hotlistSlotIndexProblem(to);
  if (problem) return refuse(problem);

  return commitChanges(
    character,
    [
      {
        change: { kind: 'swap-hotlist-slots', from, to },
        source: 'manual-edit',
        reason: `Moved Hotlist slot ${from + 1} to ${to + 1}`,
      },
    ],
    { characters: deps.characters, now: deps.now },
    { catalogue: deps.catalogue, lookupSkill: deps.lookupSkill },
  );
}

/** The first empty slot, or null when all ten are taken (RULE DCC-HOT-006:
 *  a full Hotlist needs something removed or swapped to make room). */
export function firstEmptyHotlistSlot(character: Character): number | null {
  const index = character.hotlist.findIndex((slot) => slot === null);
  return index === -1 ? null : index;
}
